/* ============================================================
   NAFS 2.0 — MODERN HERITAGE · behavior engine
   One external file, idempotent init, immune to inline-script
   stripping and soft navigations. Re-inits on DOM changes.
   ============================================================ */
(function () {
  'use strict';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function once(el, key) {
    if (!el || el.dataset['nafs' + key]) return false;
    el.dataset['nafs' + key] = '1';
    return true;
  }

  /* ---------- Shared reveal observer ---------- */
  function initReveals(scope) {
    var els = (scope || document).querySelectorAll('.nafs-reveal:not([data-nafsreveal])');
    if (!els.length) return;
    if (REDUCED) {
      els.forEach(function (el) { el.dataset.nafsreveal = '1'; el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { el.dataset.nafsreveal = '1'; io.observe(el); });
  }

  /* ---------- Hero: cinematic salon slideshow ---------- */
  function initHero() {
    document.querySelectorAll('.nafs-hero').forEach(function (root) {
      if (!once(root, 'Hero')) return;

      var title = root.querySelector('.nafs-hero__title');
      if (title && !title.dataset.split) {
        var text = title.textContent.trim();
        title.textContent = '';
        for (var i = 0; i < text.length; i++) {
          var s = document.createElement('span');
          s.className = 'ch';
          s.style.animationDelay = (0.15 + i * 0.07) + 's';
          s.textContent = text[i];
          title.appendChild(s);
        }
        title.dataset.split = '1';
      }

      var slides = Array.prototype.slice.call(root.querySelectorAll('.nafs-hero__slide'));
      var ticksWrap = root.querySelector('.nafs-hero__ticks');
      if (ticksWrap && slides.length > 1) {
        for (var t = 0; t < slides.length; t++) {
          var tick = document.createElement('i');
          if (t === 0) tick.className = 'is-on';
          ticksWrap.appendChild(tick);
        }
      }
      var ticks = ticksWrap ? Array.prototype.slice.call(ticksWrap.children) : [];

      if (REDUCED || slides.length < 2) return;

      var interval = (parseInt(root.dataset.interval, 10) || 7) * 1000;
      var idx = 0, timer = null, visible = true;

      function show(n) {
        slides[idx].classList.remove('is-active');
        if (ticks[idx]) ticks[idx].classList.remove('is-on');
        idx = n % slides.length;
        slides[idx].classList.add('is-active');
        if (ticks[idx]) ticks[idx].classList.add('is-on');
      }
      function start() {
        if (timer !== null) return;
        timer = setInterval(function () {
          if (document.hidden || !visible) return;
          show(idx + 1);
        }, interval);
      }
      start();

      var io = new IntersectionObserver(function (es) {
        visible = es[0].isIntersecting;
      }, { threshold: 0.05 });
      io.observe(root);

      /* Soft parallax on the active image — slow, lerped, never shaky */
      if (window.matchMedia('(pointer: fine)').matches) {
        var tx = 0, ty = 0, cx = 0, cy = 0, rafId = null;
        window.addEventListener('pointermove', function (e) {
          tx = (e.clientX / window.innerWidth - 0.5) * 2;
          ty = (e.clientY / window.innerHeight - 0.5) * 2;
          if (rafId === null) rafId = requestAnimationFrame(tickP);
        }, { passive: true });
        function tickP() {
          rafId = null;
          cx += (tx - cx) * 0.06;
          cy += (ty - cy) * 0.06;
          root.style.setProperty('--px', (cx * 12) + 'px');
          root.style.setProperty('--pyy', (cy * 7) + 'px');
          root.querySelectorAll('.nafs-hero__slide img').forEach(function (img) {
            img.style.setProperty('--px', (cx * 12) + 'px');
            img.style.setProperty('--pyy', (cy * 7) + 'px');
          });
          if (Math.abs(tx - cx) > 0.002 || Math.abs(ty - cy) > 0.002) {
            rafId = requestAnimationFrame(tickP);
          }
        }
      }
    });
  }

  /* ---------- Room: tilt cards ---------- */
  function initRoom() {
    if (REDUCED || !window.matchMedia('(pointer: fine)').matches) return;
    document.querySelectorAll('.nafs-card[data-tilt]').forEach(function (card) {
      if (!once(card, 'Tilt')) return;
      var r;
      card.addEventListener('pointerenter', function () { r = card.getBoundingClientRect(); });
      card.addEventListener('pointermove', function (e) {
        if (!r) r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        card.style.transform = 'perspective(900px) rotateY(' + ((px - 0.5) * 7) + 'deg) rotateX(' + ((0.5 - py) * 7) + 'deg) translateY(-4px)';
        card.style.setProperty('--gx', (px * 100) + '%');
        card.style.setProperty('--gy', (py * 100) + '%');
      });
      card.addEventListener('pointerleave', function () { card.style.transform = ''; r = null; });
    });
  }

  /* ---------- Manifesto: border draw ---------- */
  function initManifesto() {
    document.querySelectorAll('.nafs-manifesto__inner').forEach(function (wrap) {
      if (!once(wrap, 'Manifesto')) return;
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.isIntersecting) { wrap.classList.add('is-in'); io.disconnect(); }
        });
      }, { threshold: 0.3 });
      io.observe(wrap);
    });
  }

  /* ---------- Diptych: counters + parallax ---------- */
  function initDiptych() {
    document.querySelectorAll('.nafs-diptych').forEach(function (root) {
      if (!once(root, 'Diptych')) return;

      var counted = false;
      var cio = new IntersectionObserver(function (es) {
        if (!es[0].isIntersecting || counted) return;
        counted = true;
        root.querySelectorAll('[data-count]').forEach(function (el) {
          var target = parseInt(el.dataset.count, 10) || 0;
          if (REDUCED) { el.textContent = target; return; }
          var t0 = null;
          function step(ts) {
            if (!t0) t0 = ts;
            var p = Math.min((ts - t0) / 1400, 1);
            el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
        cio.disconnect();
      }, { threshold: 0.4 });
      cio.observe(root);

      if (REDUCED) return;
      var img = root.querySelector('[data-parallax]');
      if (!img) return;
      var active = false, rafId = null;
      function tick() {
        if (!active || document.hidden) { rafId = null; return; }
        var r = root.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = Math.max(0, Math.min(1, (vh - r.top) / (vh + r.height)));
        img.style.setProperty('--py', (-(p * 14)) + '%');
        rafId = requestAnimationFrame(tick);
      }
      var pio = new IntersectionObserver(function (es) {
        active = es[0].isIntersecting;
        if (active && rafId === null) rafId = requestAnimationFrame(tick);
      }, { threshold: 0 });
      pio.observe(root);
    });
  }

  /* ---------- Corridor: scroll-driven gallery ---------- */
  function initCorridor() {
    document.querySelectorAll('.nafs-corridor').forEach(function (root) {
      if (!once(root, 'Corridor')) return;
      if (REDUCED) return;

      var sticky = root.querySelector('.nafs-corridor__sticky');
      var track = root.querySelector('.nafs-corridor__track');
      var bar = root.querySelector('.nafs-corridor__progress i');
      if (!sticky || !track) return;

      function update() {
        if (getComputedStyle(sticky).position !== 'sticky') {
          track.style.transform = '';
          return;
        }
        var r = root.getBoundingClientRect();
        var total = r.height - window.innerHeight;
        if (total <= 0) return;
        var p = Math.max(0, Math.min(1, -r.top / total));
        var shift = Math.max(0, track.scrollWidth - window.innerWidth + window.innerWidth * 0.08);
        track.style.transform = 'translateX(' + (-p * shift) + 'px)';
        if (bar) bar.style.setProperty('--p', p);
      }

      var active = false, rafId = null;
      function tick() {
        if (!active || document.hidden) { rafId = null; return; }
        update();
        rafId = requestAnimationFrame(tick);
      }
      var io = new IntersectionObserver(function (es) {
        active = es[0].isIntersecting;
        if (active && rafId === null) rafId = requestAnimationFrame(tick);
      }, { threshold: 0 });
      io.observe(root);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden && active && rafId === null) rafId = requestAnimationFrame(tick);
      });
      update();
    });
  }

  /* ---------- Boot + re-init on soft navigation ---------- */
  function initAll() {
    initReveals();
    initHero();
    initRoom();
    initManifesto();
    initDiptych();
    initCorridor();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  window.addEventListener('load', initAll);
  document.addEventListener('shopify:section:load', initAll);

  /* Horizon view-transition / soft-nav safety net */
  var main = null, debounce = null;
  function watchMain() {
    var m = document.getElementById('MainContent');
    if (!m || m === main) return;
    main = m;
    new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(initAll, 120);
    }).observe(m, { childList: true, subtree: true });
  }
  watchMain();
  document.addEventListener('DOMContentLoaded', watchMain);
})();
